import { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAdminAuth } from '@/context/AdminAuthContext';
import axios from 'axios';
import { Package, MessageSquare, BarChart3, LogOut, Plus, Pencil, Trash2, X, Save, FileText, Settings, ChevronDown, ChevronUp, ShoppingCart, IndianRupee, Lock, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function formatApiError(err) {
  const d = err.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(e => e.msg || JSON.stringify(e)).join(' ');
  return err.message || 'Something went wrong';
}

export default function AdminDashboard() {
  const { admin, loading, logout, authHeaders } = useAdminAuth();
  const [tab, setTab] = useState('products');

  if (loading) return <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center"><p className="text-[#777] font-sans text-sm">Loading...</p></div>;
  if (!admin) return <Navigate to="/admin" replace />;

  const headers = authHeaders();

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="admin-dashboard">
      <header className="bg-[#0C447C] h-14 flex items-center px-6 justify-between">
        <div className="flex items-center gap-3">
          <span className="font-serif font-bold text-[18px] leading-none tracking-[0.04em]">
            <span className="text-white">TRU</span><span style={{ color: 'rgba(255,255,255,0.45)' }}>FORM</span>
          </span>
          <span className="text-[10px] font-sans uppercase tracking-[2px] text-white/40 ml-2">Admin</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-[12px] font-sans text-white/50">{admin.email}</span>
          <button data-testid="admin-logout-btn" onClick={logout} className="text-white/40 hover:text-white transition-colors" title="Logout">
            <LogOut size={16} />
          </button>
        </div>
      </header>

      <div className="bg-white border-b border-[#E8ECF0] px-6 overflow-x-auto">
        <div className="max-w-6xl mx-auto flex gap-0">
          {[
            { key: 'products', label: 'Products', icon: Package },
            { key: 'orders', label: 'Orders', icon: ShoppingCart },
            { key: 'articles', label: 'Articles', icon: FileText },
            { key: 'content', label: 'Content', icon: Settings },
            { key: 'contacts', label: 'Messages', icon: MessageSquare },
            { key: 'waitlist', label: 'Waitlist', icon: Mail },
            { key: 'stats', label: 'Overview', icon: BarChart3 },
            { key: 'settings', label: 'Settings', icon: Lock },
          ].map(t => (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-5 py-3.5 text-[11px] font-sans font-medium uppercase tracking-[2px] border-b-2 transition-colors whitespace-nowrap ${
                tab === t.key ? 'border-[#0C447C] text-[#0C447C]' : 'border-transparent text-[#999] hover:text-[#666]'
              }`}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {tab === 'products' && <ProductsTab headers={headers} />}
        {tab === 'orders' && <OrdersTab headers={headers} />}
        {tab === 'articles' && <ArticlesTab headers={headers} />}
        {tab === 'content' && <ContentTab headers={headers} />}
        {tab === 'contacts' && <ContactsTab headers={headers} />}
        {tab === 'waitlist' && <WaitlistTab headers={headers} />}
        {tab === 'stats' && <StatsTab headers={headers} />}
        {tab === 'settings' && <SettingsTab headers={headers} />}
      </div>
    </div>
  );
}

// ============ REUSABLE FORM FIELDS ============

function Field({ label, testid, value, onChange, className = '', type = 'text', required = false, disabled = false, placeholder = '' }) {
  return (
    <div className={className}>
      <label className="block text-[11px] font-sans font-medium uppercase tracking-[2px] text-[#0C447C] mb-1.5">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={e => onChange(e.target.value)} required={required} disabled={disabled}
        placeholder={placeholder}
        className="w-full px-3 py-2.5 border border-[#E0E0E0] rounded-[2px] text-sm font-sans text-[#1A1A1A] focus:outline-none focus:border-[#0C447C] transition-colors disabled:bg-[#F5F7FA] disabled:text-[#999]" />
    </div>
  );
}

function FieldArea({ label, testid, value, onChange, rows = 3, className = '' }) {
  return (
    <div className={className}>
      <label className="block text-[11px] font-sans font-medium uppercase tracking-[2px] text-[#0C447C] mb-1.5">{label}</label>
      <textarea data-testid={testid} value={value} onChange={e => onChange(e.target.value)} rows={rows}
        className="w-full px-3 py-2.5 border border-[#E0E0E0] rounded-[2px] text-sm font-sans text-[#1A1A1A] focus:outline-none focus:border-[#0C447C] transition-colors resize-y" />
    </div>
  );
}

function StatusMsg({ error, success }) {
  if (error) return <div className="bg-red-50 border border-red-200 text-red-700 text-[12px] font-sans px-3 py-2 rounded-[2px] mb-4">{error}</div>;
  if (success) return <div className="bg-green-50 border border-green-200 text-green-700 text-[12px] font-sans px-3 py-2 rounded-[2px] mb-4">{success}</div>;
  return null;
}

// ============ PRODUCTS TAB ============

function ProductsTab({ headers }) {
  const [products, setProducts] = useState([]);
  const [loadingP, setLoadingP] = useState(true);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');

  const fetchProducts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/products`, { headers });
      setProducts(data);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoadingP(false); }
  }, [headers]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleDelete = async (slug) => {
    if (!window.confirm(`Delete "${slug}"?`)) return;
    try {
      await axios.delete(`${API}/api/admin/products/${slug}`, { headers });
      setProducts(prev => prev.filter(p => p.slug !== slug));
    } catch (err) { setError(formatApiError(err)); }
  };

  if (editing) {
    return <ProductForm slug={editing === 'new' ? null : editing} headers={headers}
      onDone={() => { setEditing(null); fetchProducts(); }} onCancel={() => setEditing(null)} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-serif font-medium text-[#1A1A1A]">Products ({products.length})</h2>
        <button data-testid="add-product-btn" onClick={() => setEditing('new')}
          className="flex items-center gap-2 bg-[#0C447C] text-white px-4 py-2 rounded-[2px] text-[11px] font-sans font-medium uppercase tracking-[2px] hover:bg-[#082F5A] transition-colors">
          <Plus size={14} /> Add Product
        </button>
      </div>
      <StatusMsg error={error} />
      {loadingP ? <p className="text-[#999] text-sm font-sans">Loading...</p> : products.length === 0 ? (
        <div className="bg-white rounded-[2px] border border-[#E8ECF0] p-10 text-center">
          <Package size={32} className="text-[#ccc] mx-auto mb-3" />
          <p className="text-[#999] font-sans text-sm">No products yet.</p>
        </div>
      ) : (
        <div className="grid gap-2">
          {products.map(p => (
            <div key={p.slug} data-testid={`admin-product-${p.slug}`}
              className="bg-white rounded-[2px] border border-[#E8ECF0] p-4 flex items-center justify-between hover:border-[#0C447C]/20 transition-colors">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                {p.image && <img src={p.image} alt="" className="w-12 h-12 object-cover rounded-[2px] bg-[#F5F7FA] flex-shrink-0" />}
                <div className="min-w-0">
                  <h3 className="font-serif text-[15px] text-[#1A1A1A] font-medium truncate">{p.name}</h3>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-[10px] font-sans uppercase tracking-[2px] text-[#0C447C] bg-[#EBF0F5] px-2 py-0.5 rounded-[2px]">{p.tier}</span>
                    <span className="text-[13px] font-sans text-[#777]">&#8377;{p.price} / {p.size}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                <button data-testid={`edit-${p.slug}`} onClick={() => setEditing(p.slug)} className="p-2 text-[#999] hover:text-[#0C447C] hover:bg-[#F5F7FA] rounded-[2px] transition-colors"><Pencil size={14} /></button>
                <button data-testid={`delete-${p.slug}`} onClick={() => handleDelete(p.slug)} className="p-2 text-[#999] hover:text-red-600 hover:bg-red-50 rounded-[2px] transition-colors"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const EMPTY_PRODUCT = { name: '', slug: '', tier: 'daily', price: '', size: '', image: '', tags: [], description: '', ingredients: [], surfactant: '', ph: '', how_to_use: [], honest_version: '' };

function ProductForm({ slug, headers, onDone, onCancel }) {
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [tagsStr, setTagsStr] = useState('');
  const [howToStr, setHowToStr] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isNew = !slug;

  useEffect(() => {
    if (!slug) return;
    axios.get(`${API}/api/admin/products`, { headers }).then(({ data }) => {
      const p = data.find(x => x.slug === slug);
      if (p) { setForm({ ...EMPTY_PRODUCT, ...p, price: String(p.price) }); setTagsStr((p.tags || []).join(', ')); setHowToStr((p.how_to_use || []).join('\n')); }
    });
  }, [slug, headers]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const addIngredient = () => set('ingredients', [...form.ingredients, { name: '', percentage: '', reason: '' }]);
  const updateIngredient = (i, k, v) => { const u = [...form.ingredients]; u[i] = { ...u[i], [k]: v }; set('ingredients', u); };
  const removeIngredient = (i) => set('ingredients', form.ingredients.filter((_, idx) => idx !== i));

  const handleSave = async (e) => {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0, tags: tagsStr.split(',').map(t => t.trim()).filter(Boolean), how_to_use: howToStr.split('\n').map(s => s.trim()).filter(Boolean) };
      if (isNew) await axios.post(`${API}/api/admin/products`, payload, { headers });
      else await axios.put(`${API}/api/admin/products/${slug}`, payload, { headers });
      onDone();
    } catch (err) { setError(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-serif font-medium text-[#1A1A1A]">{isNew ? 'Add Product' : 'Edit Product'}</h2>
        <button onClick={onCancel} className="text-[#999] hover:text-[#1A1A1A]" data-testid="cancel-edit-btn"><X size={20} /></button>
      </div>
      <StatusMsg error={error} />
      <form onSubmit={handleSave} data-testid="product-form" className="bg-white rounded-[2px] border border-[#E8ECF0] p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Field label="Product Name" testid="field-name" value={form.name} onChange={v => set('name', v)} required />
          <Field label="Slug" testid="field-slug" value={form.slug} onChange={v => set('slug', v)} required disabled={!isNew} placeholder="e.g. daily-face-wash" />
          <div>
            <label className="block text-[11px] font-sans font-medium uppercase tracking-[2px] text-[#0C447C] mb-1.5">Tier</label>
            <select data-testid="field-tier" value={form.tier} onChange={e => set('tier', e.target.value)}
              className="w-full px-3 py-2.5 border border-[#E0E0E0] rounded-[2px] text-sm font-sans text-[#1A1A1A] focus:outline-none focus:border-[#0C447C]">
              <option value="daily">Daily</option><option value="pro">Pro</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Price" testid="field-price" type="number" value={form.price} onChange={v => set('price', v)} required />
            <Field label="Size" testid="field-size" value={form.size} onChange={v => set('size', v)} placeholder="e.g. 100ml" />
          </div>
        </div>
        <Field label="Image URL" testid="field-image" value={form.image} onChange={v => set('image', v)} className="mb-4" />
        <FieldArea label="Description" testid="field-description" value={form.description} onChange={v => set('description', v)} rows={2} className="mb-4" />
        <Field label="Tags (comma separated)" testid="field-tags" value={tagsStr} onChange={setTagsStr} className="mb-4" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Field label="Surfactant" testid="field-surfactant" value={form.surfactant} onChange={v => set('surfactant', v)} />
          <Field label="pH Level" testid="field-ph" value={form.ph} onChange={v => set('ph', v)} />
        </div>
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-[11px] font-sans font-medium uppercase tracking-[2px] text-[#0C447C]">Ingredients</label>
            <button type="button" data-testid="add-ingredient-btn" onClick={addIngredient} className="text-[10px] font-sans text-[#0C447C] hover:text-[#082F5A] flex items-center gap-1 uppercase tracking-[2px]"><Plus size={12} /> Add</button>
          </div>
          {form.ingredients.map((ing, i) => (
            <div key={i} className="flex gap-2 mb-2 items-start">
              <input data-testid={`ingredient-name-${i}`} value={ing.name} onChange={e => updateIngredient(i, 'name', e.target.value)} className="flex-1 px-2 py-2 border border-[#E0E0E0] rounded-[2px] text-sm font-sans" placeholder="Name" />
              <input data-testid={`ingredient-pct-${i}`} value={ing.percentage} onChange={e => updateIngredient(i, 'percentage', e.target.value)} className="w-20 px-2 py-2 border border-[#E0E0E0] rounded-[2px] text-sm font-sans" placeholder="%" />
              <input data-testid={`ingredient-reason-${i}`} value={ing.reason} onChange={e => updateIngredient(i, 'reason', e.target.value)} className="flex-[2] px-2 py-2 border border-[#E0E0E0] rounded-[2px] text-sm font-sans" placeholder="Reason" />
              <button type="button" onClick={() => removeIngredient(i)} className="p-2 text-[#999] hover:text-red-600"><X size={14} /></button>
            </div>
          ))}
        </div>
        <FieldArea label="How to Use (one step per line)" testid="field-howto" value={howToStr} onChange={setHowToStr} rows={3} className="mb-4" />
        <FieldArea label="Honest Version" testid="field-honest" value={form.honest_version} onChange={v => set('honest_version', v)} rows={4} className="mb-6" />
        <div className="flex gap-3">
          <button data-testid="save-product-btn" type="submit" disabled={saving}
            className="flex items-center gap-2 bg-[#0C447C] text-white px-6 py-2.5 rounded-[2px] text-[11px] font-sans font-medium uppercase tracking-[2px] hover:bg-[#082F5A] transition-colors disabled:opacity-50">
            <Save size={14} /> {saving ? 'Saving...' : (isNew ? 'Create Product' : 'Save Changes')}
          </button>
          <button type="button" onClick={onCancel} className="px-6 py-2.5 rounded-[2px] text-[11px] font-sans font-medium uppercase tracking-[2px] text-[#999] border border-[#E0E0E0] hover:text-[#666] hover:border-[#ccc] transition-colors">Cancel</button>
        </div>
      </form>
    </div>
  );
}

// ============ ARTICLES TAB ============

function ArticlesTab({ headers }) {
  const [articles, setArticles] = useState([]);
  const [loadingA, setLoadingA] = useState(true);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');

  const fetch = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/admin/articles`, { headers }); setArticles(data); }
    catch (err) { setError(formatApiError(err)); } finally { setLoadingA(false); }
  }, [headers]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleDelete = async (slug) => {
    if (!window.confirm(`Delete article "${slug}"?`)) return;
    try { await axios.delete(`${API}/api/admin/articles/${slug}`, { headers }); setArticles(prev => prev.filter(a => a.slug !== slug)); }
    catch (err) { setError(formatApiError(err)); }
  };

  if (editing) {
    return <ArticleForm slug={editing === 'new' ? null : editing} headers={headers}
      onDone={() => { setEditing(null); fetch(); }} onCancel={() => setEditing(null)} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-serif font-medium text-[#1A1A1A]">Science Articles ({articles.length})</h2>
        <button data-testid="add-article-btn" onClick={() => setEditing('new')}
          className="flex items-center gap-2 bg-[#0C447C] text-white px-4 py-2 rounded-[2px] text-[11px] font-sans font-medium uppercase tracking-[2px] hover:bg-[#082F5A] transition-colors">
          <Plus size={14} /> Add Article
        </button>
      </div>
      <StatusMsg error={error} />
      {loadingA ? <p className="text-[#999] text-sm font-sans">Loading...</p> : articles.length === 0 ? (
        <div className="bg-white rounded-[2px] border border-[#E8ECF0] p-10 text-center">
          <FileText size={32} className="text-[#ccc] mx-auto mb-3" />
          <p className="text-[#999] font-sans text-sm">No articles yet.</p>
        </div>
      ) : (
        <div className="grid gap-2">
          {articles.map(a => (
            <div key={a.slug} data-testid={`admin-article-${a.slug}`}
              className="bg-white rounded-[2px] border border-[#E8ECF0] p-4 flex items-center justify-between hover:border-[#0C447C]/20 transition-colors">
              <div className="min-w-0 flex-1">
                <h3 className="font-serif text-[15px] text-[#1A1A1A] font-medium truncate">{a.title}</h3>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className="text-[10px] font-sans uppercase tracking-[2px] text-[#0C447C] bg-[#EBF0F5] px-2 py-0.5 rounded-[2px]">{a.category}</span>
                  <span className="text-[12px] font-sans text-[#999]">{a.read_time}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                <button data-testid={`edit-article-${a.slug}`} onClick={() => setEditing(a.slug)} className="p-2 text-[#999] hover:text-[#0C447C] hover:bg-[#F5F7FA] rounded-[2px] transition-colors"><Pencil size={14} /></button>
                <button data-testid={`delete-article-${a.slug}`} onClick={() => handleDelete(a.slug)} className="p-2 text-[#999] hover:text-red-600 hover:bg-red-50 rounded-[2px] transition-colors"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ArticleForm({ slug, headers, onDone, onCancel }) {
  const [form, setForm] = useState({ title: '', slug: '', excerpt: '', category: '', read_time: '', content: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isNew = !slug;

  useEffect(() => {
    if (!slug) return;
    axios.get(`${API}/api/articles/${slug}`, { headers }).then(({ data }) => setForm(data)).catch(err => console.error('API request failed:', err));
  }, [slug, headers]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      if (isNew) await axios.post(`${API}/api/admin/articles`, form, { headers });
      else await axios.put(`${API}/api/admin/articles/${slug}`, form, { headers });
      onDone();
    } catch (err) { setError(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-serif font-medium text-[#1A1A1A]">{isNew ? 'Add Article' : 'Edit Article'}</h2>
        <button onClick={onCancel} className="text-[#999] hover:text-[#1A1A1A]" data-testid="cancel-article-btn"><X size={20} /></button>
      </div>
      <StatusMsg error={error} />
      <form onSubmit={handleSave} data-testid="article-form" className="bg-white rounded-[2px] border border-[#E8ECF0] p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Field label="Title" testid="article-title" value={form.title} onChange={v => set('title', v)} required />
          <Field label="Slug" testid="article-slug" value={form.slug} onChange={v => set('slug', v)} required disabled={!isNew} placeholder="e.g. my-article" />
          <Field label="Category" testid="article-category" value={form.category} onChange={v => set('category', v)} placeholder="e.g. FORMULATION" />
          <Field label="Read Time" testid="article-readtime" value={form.read_time} onChange={v => set('read_time', v)} placeholder="e.g. 5 min read" />
        </div>
        <FieldArea label="Excerpt" testid="article-excerpt" value={form.excerpt} onChange={v => set('excerpt', v)} rows={2} className="mb-4" />
        <FieldArea label="Content (Markdown supported)" testid="article-content" value={form.content} onChange={v => set('content', v)} rows={12} className="mb-6" />
        <div className="flex gap-3">
          <button data-testid="save-article-btn" type="submit" disabled={saving}
            className="flex items-center gap-2 bg-[#0C447C] text-white px-6 py-2.5 rounded-[2px] text-[11px] font-sans font-medium uppercase tracking-[2px] hover:bg-[#082F5A] transition-colors disabled:opacity-50">
            <Save size={14} /> {saving ? 'Saving...' : (isNew ? 'Create Article' : 'Save Changes')}
          </button>
          <button type="button" onClick={onCancel} className="px-6 py-2.5 rounded-[2px] text-[11px] font-sans font-medium uppercase tracking-[2px] text-[#999] border border-[#E0E0E0] hover:text-[#666] hover:border-[#ccc] transition-colors">Cancel</button>
        </div>
      </form>
    </div>
  );
}

// ============ CONTENT TAB ============

function ContentTab({ headers }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [openSection, setOpenSection] = useState('announcement');

  useEffect(() => {
    axios.get(`${API}/api/admin/site-content`, { headers })
      .then(({ data }) => setContent(data))
      .catch(err => console.error('API request failed:', err)).finally(() => setLoading(false));
  }, [headers]);

  const save = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const { data } = await axios.put(`${API}/api/admin/site-content`, content, { headers });
      setContent(data);
      setSuccess('Content saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading || !content) return <p className="text-[#999] text-sm font-sans">Loading...</p>;

  const ann = content.announcement || {};
  const hp = content.homepage || {};
  const ab = content.about || {};

  const setAnn = (k, v) => setContent(prev => ({ ...prev, announcement: { ...prev.announcement, [k]: v } }));
  const setHp = (k, v) => setContent(prev => ({ ...prev, homepage: { ...prev.homepage, [k]: v } }));
  const setAb = (k, v) => setContent(prev => ({ ...prev, about: { ...prev.about, [k]: v } }));

  const toggle = (s) => setOpenSection(prev => prev === s ? null : s);

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-serif font-medium text-[#1A1A1A]">Website Content</h2>
        <button data-testid="save-content-btn" onClick={save} disabled={saving}
          className="flex items-center gap-2 bg-[#0C447C] text-white px-5 py-2 rounded-[2px] text-[11px] font-sans font-medium uppercase tracking-[2px] hover:bg-[#082F5A] transition-colors disabled:opacity-50">
          <Save size={14} /> {saving ? 'Saving...' : 'Save All Changes'}
        </button>
      </div>
      <StatusMsg error={error} success={success} />

      {/* Announcement Bar */}
      <Section title="Announcement Bar" open={openSection === 'announcement'} onToggle={() => toggle('announcement')} testid="section-announcement">
        <Field label="Banner Text" testid="ann-text" value={ann.text || ''} onChange={v => setAnn('text', v)}
          placeholder="Free shipping above ₹499 · Delivered in 3-5 days" />
      </Section>

      {/* Homepage */}
      <Section title="Homepage" open={openSection === 'homepage'} onToggle={() => toggle('homepage')} testid="section-homepage">
        <HomepageContentEditor hp={hp} setHp={setHp} />
      </Section>

      {/* About Page */}
      <Section title="About Page" open={openSection === 'about'} onToggle={() => toggle('about')} testid="section-about">
        <AboutContentEditor ab={ab} setAb={setAb} />
      </Section>
    </div>
  );
}

function HomepageContentEditor({ hp, setHp }) {
  return (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Hero Line 1" testid="hp-hero1" value={hp.hero_caption_line1 || ''} onChange={v => setHp('hero_caption_line1', v)} />
            <Field label="Hero Line 2 (italic)" testid="hp-hero2" value={hp.hero_caption_line2 || ''} onChange={v => setHp('hero_caption_line2', v)} />
          </div>
          <Field label="Tagline" testid="hp-tagline" value={hp.tagline || ''} onChange={v => setHp('tagline', v)} />

          {/* Stats */}
          <div>
            <label className="block text-[11px] font-sans font-medium uppercase tracking-[2px] text-[#0C447C] mb-2">Stat Cards (4)</label>
            {(hp.stats || []).map((s, i) => (
              <div key={i} className="grid grid-cols-3 gap-2 mb-2">
                <input value={s.val} onChange={e => { const u = [...hp.stats]; u[i] = { ...u[i], val: e.target.value }; setHp('stats', u); }}
                  className="px-2 py-2 border border-[#E0E0E0] rounded-[2px] text-sm font-sans" placeholder="Value" data-testid={`stat-val-${i}`} />
                <input value={s.label} onChange={e => { const u = [...hp.stats]; u[i] = { ...u[i], label: e.target.value }; setHp('stats', u); }}
                  className="px-2 py-2 border border-[#E0E0E0] rounded-[2px] text-sm font-sans" placeholder="Label" data-testid={`stat-label-${i}`} />
                <input value={s.note} onChange={e => { const u = [...hp.stats]; u[i] = { ...u[i], note: e.target.value }; setHp('stats', u); }}
                  className="px-2 py-2 border border-[#E0E0E0] rounded-[2px] text-sm font-sans" placeholder="Note" data-testid={`stat-note-${i}`} />
              </div>
            ))}
          </div>

          {/* USPs */}
          <div>
            <label className="block text-[11px] font-sans font-medium uppercase tracking-[2px] text-[#0C447C] mb-2">USP Grid (6)</label>
            {(hp.usps || []).map((u, i) => (
              <div key={i} className="grid grid-cols-2 gap-2 mb-2">
                <input value={u.label} onChange={e => { const arr = [...hp.usps]; arr[i] = { ...arr[i], label: e.target.value }; setHp('usps', arr); }}
                  className="px-2 py-2 border border-[#E0E0E0] rounded-[2px] text-sm font-sans" placeholder="Label" data-testid={`usp-label-${i}`} />
                <input value={u.subtitle} onChange={e => { const arr = [...hp.usps]; arr[i] = { ...arr[i], subtitle: e.target.value }; setHp('usps', arr); }}
                  className="px-2 py-2 border border-[#E0E0E0] rounded-[2px] text-sm font-sans" placeholder="Subtitle" data-testid={`usp-sub-${i}`} />
              </div>
            ))}
          </div>

          {/* Product Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Product Eyebrow" testid="hp-prod-eyebrow" value={hp.product_section_eyebrow || ''} onChange={v => setHp('product_section_eyebrow', v)} />
            <Field label="Product Heading" testid="hp-prod-heading" value={hp.product_section_heading || ''} onChange={v => setHp('product_section_heading', v)} />
            <Field label="Product Subheading" testid="hp-prod-sub" value={hp.product_section_subheading || ''} onChange={v => setHp('product_section_subheading', v)} />
          </div>

          {/* Routine */}
          <Field label="Routine Heading" testid="hp-routine-heading" value={hp.routine_heading || ''} onChange={v => setHp('routine_heading', v)} />
          <div>
            <label className="block text-[11px] font-sans font-medium uppercase tracking-[2px] text-[#0C447C] mb-2">Routine Steps</label>
            {(hp.routine_steps || []).map((s, i) => (
              <div key={i} className="grid grid-cols-3 gap-2 mb-2">
                <input value={s.title} onChange={e => { const u = [...hp.routine_steps]; u[i] = { ...u[i], title: e.target.value }; setHp('routine_steps', u); }}
                  className="px-2 py-2 border border-[#E0E0E0] rounded-[2px] text-sm font-sans" placeholder="Title" data-testid={`step-title-${i}`} />
                <input value={s.time} onChange={e => { const u = [...hp.routine_steps]; u[i] = { ...u[i], time: e.target.value }; setHp('routine_steps', u); }}
                  className="px-2 py-2 border border-[#E0E0E0] rounded-[2px] text-sm font-sans" placeholder="Time" data-testid={`step-time-${i}`} />
                <input value={s.desc} onChange={e => { const u = [...hp.routine_steps]; u[i] = { ...u[i], desc: e.target.value }; setHp('routine_steps', u); }}
                  className="px-2 py-2 border border-[#E0E0E0] rounded-[2px] text-sm font-sans" placeholder="Description" data-testid={`step-desc-${i}`} />
              </div>
            ))}
          </div>

          {/* Science CTA */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Science CTA Heading" testid="hp-sci-heading" value={hp.science_cta_heading || ''} onChange={v => setHp('science_cta_heading', v)} />
            <Field label="Science CTA Subheading" testid="hp-sci-sub" value={hp.science_cta_subheading || ''} onChange={v => setHp('science_cta_subheading', v)} />
          </div>

          {/* Testimonials */}
          <Field label="Testimonials Heading" testid="hp-test-heading" value={hp.testimonials_heading || ''} onChange={v => setHp('testimonials_heading', v)} />
          <div>
            <label className="block text-[11px] font-sans font-medium uppercase tracking-[2px] text-[#0C447C] mb-2">Testimonials</label>
            {(hp.testimonials || []).map((t, i) => (
              <div key={i} className="grid grid-cols-3 gap-2 mb-2">
                <input value={t.quote} onChange={e => { const u = [...hp.testimonials]; u[i] = { ...u[i], quote: e.target.value }; setHp('testimonials', u); }}
                  className="col-span-1 md:col-span-1 px-2 py-2 border border-[#E0E0E0] rounded-[2px] text-sm font-sans" placeholder="Quote" data-testid={`test-quote-${i}`} />
                <input value={t.name} onChange={e => { const u = [...hp.testimonials]; u[i] = { ...u[i], name: e.target.value }; setHp('testimonials', u); }}
                  className="px-2 py-2 border border-[#E0E0E0] rounded-[2px] text-sm font-sans" placeholder="Name" data-testid={`test-name-${i}`} />
                <input value={t.city} onChange={e => { const u = [...hp.testimonials]; u[i] = { ...u[i], city: e.target.value }; setHp('testimonials', u); }}
                  className="px-2 py-2 border border-[#E0E0E0] rounded-[2px] text-sm font-sans" placeholder="City" data-testid={`test-city-${i}`} />
              </div>
            ))}
          </div>
        </div>
  );
}

function AboutContentEditor({ ab, setAb }) {
  return (
        <div className="space-y-4">
          <Field label="Page Heading" testid="ab-heading" value={ab.heading || ''} onChange={v => setAb('heading', v)} />
          <FieldArea label="Story Paragraphs (one per line)" testid="ab-story" rows={5}
            value={(ab.story_paragraphs || []).join('\n')}
            onChange={v => setAb('story_paragraphs', v.split('\n'))} />
          <FieldArea label="Commitments (one per line)" testid="ab-commitments" rows={6}
            value={(ab.commitments || []).join('\n')}
            onChange={v => setAb('commitments', v.split('\n').filter(Boolean))} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Instagram Handle" testid="ab-insta" value={ab.instagram_handle || ''} onChange={v => setAb('instagram_handle', v)} />
            <Field label="Instagram URL" testid="ab-insta-url" value={ab.instagram_url || ''} onChange={v => setAb('instagram_url', v)} />
            <Field label="Contact Email" testid="ab-email" value={ab.email || ''} onChange={v => setAb('email', v)} />
          </div>
        </div>
  );
}

function Section({ title, open, onToggle, children, testid }) {
  return (
    <div className="bg-white rounded-[2px] border border-[#E8ECF0] mb-3" data-testid={testid}>
      <button onClick={onToggle} className="w-full flex items-center justify-between px-5 py-4 text-left" data-testid={`${testid}-toggle`}>
        <span className="font-serif text-[15px] text-[#1A1A1A] font-medium">{title}</span>
        {open ? <ChevronUp size={16} className="text-[#999]" /> : <ChevronDown size={16} className="text-[#999]" />}
      </button>
      {open && <div className="px-5 pb-5 border-t border-[#E8ECF0] pt-4">{children}</div>}
    </div>
  );
}


// ============ ORDERS TAB ============

const STATUS_COLORS = {
  created: 'bg-[#EBF0F5] text-[#777]',
  confirmed: 'bg-blue-50 text-blue-700',
  processing: 'bg-yellow-50 text-yellow-700',
  shipped: 'bg-purple-50 text-purple-700',
  delivered: 'bg-green-50 text-green-700',
  cancelled: 'bg-red-50 text-red-700',
};

const PAYMENT_COLORS = {
  pending: 'bg-yellow-50 text-yellow-700',
  paid: 'bg-green-50 text-green-700',
  failed: 'bg-red-50 text-red-700',
};

function OrdersTab({ headers }) {
  const [orders, setOrders] = useState([]);
  const [loadingO, setLoadingO] = useState(true);
  const [error, setError] = useState('');

  const fetchOrders = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/admin/orders`, { headers }); setOrders(data); }
    catch (err) { setError(formatApiError(err)); } finally { setLoadingO(false); }
  }, [headers]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/api/admin/orders/${orderId}/status`, { order_status: newStatus }, { headers });
      fetchOrders();
    } catch (err) { setError(formatApiError(err)); }
  };

  if (loadingO) return <p className="text-[#999] text-sm font-sans">Loading orders...</p>;

  return (
    <div>
      <h2 className="text-lg font-serif font-medium text-[#1A1A1A] mb-5">Orders ({orders.length})</h2>
      <StatusMsg error={error} />
      {orders.length === 0 ? (
        <div className="bg-white rounded-[2px] border border-[#E8ECF0] p-10 text-center">
          <ShoppingCart size={32} className="text-[#ccc] mx-auto mb-3" />
          <p className="text-[#999] font-sans text-sm">No orders yet.</p>
        </div>
      ) : (
        <div className="grid gap-2">
          {orders.map(o => (
            <div key={o.order_id} data-testid={`admin-order-${o.order_id}`}
              className="bg-white rounded-[2px] border border-[#E8ECF0] p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <span className="font-sans text-[14px] text-[#1A1A1A] font-medium">{o.order_id}</span>
                  <span className="text-[12px] text-[#999] font-sans ml-3">{o.customer?.name}</span>
                  <span className="text-[12px] text-[#bbb] font-sans ml-2">{o.customer?.email}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-[9px] font-sans uppercase tracking-[2px] px-2 py-0.5 rounded-[2px] ${PAYMENT_COLORS[o.payment_status] || ''}`}>
                    {o.payment_status}
                  </span>
                  <span className={`text-[9px] font-sans uppercase tracking-[2px] px-2 py-0.5 rounded-[2px] ${STATUS_COLORS[o.order_status] || ''}`}>
                    {o.order_status}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-[13px] font-sans text-[#1A1A1A]">&#8377;{o.total_amount}</span>
                  <span className="text-[11px] text-[#999] font-sans">{(o.items || []).length} item{(o.items || []).length !== 1 ? 's' : ''}</span>
                  <span className="text-[11px] text-[#bbb] font-sans">{new Date(o.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                </div>
                {o.payment_status === 'paid' && o.order_status !== 'delivered' && o.order_status !== 'cancelled' && (
                  <select data-testid={`order-status-${o.order_id}`} value={o.order_status}
                    onChange={e => updateStatus(o.order_id, e.target.value)}
                    className="text-[11px] font-sans border border-[#E0E0E0] rounded-[2px] px-2 py-1 focus:outline-none focus:border-[#0C447C]">
                    <option value="confirmed">Confirmed</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ CONTACTS TAB ============

function ContactsTab({ headers }) {
  const [contacts, setContacts] = useState([]);
  const [loadingC, setLoadingC] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/admin/contacts`, { headers }).then(({ data }) => setContacts(data)).catch(err => console.error('API request failed:', err)).finally(() => setLoadingC(false));
  }, [headers]);

  if (loadingC) return <p className="text-[#999] text-sm font-sans">Loading...</p>;

  return (
    <div>
      <h2 className="text-lg font-serif font-medium text-[#1A1A1A] mb-5">Contact Messages ({contacts.length})</h2>
      {contacts.length === 0 ? (
        <div className="bg-white rounded-[2px] border border-[#E8ECF0] p-10 text-center">
          <MessageSquare size={32} className="text-[#ccc] mx-auto mb-3" />
          <p className="text-[#999] font-sans text-sm">No messages yet.</p>
        </div>
      ) : (
        <div className="grid gap-2">
          {contacts.map((c, i) => (
            <div key={c.id || i} data-testid={`contact-${i}`} className="bg-white rounded-[2px] border border-[#E8ECF0] p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <span className="font-sans text-[14px] text-[#1A1A1A] font-medium">{c.name}</span>
                  <span className="text-[12px] text-[#999] font-sans ml-3">{c.email}</span>
                </div>
                <span className="text-[11px] text-[#bbb] font-sans flex-shrink-0">{new Date(c.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
              </div>
              <p className="text-[13px] text-[#555] font-sans leading-relaxed">{c.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ WAITLIST TAB ============

function WaitlistTab({ headers }) {
  const [entries, setEntries] = useState([]);
  const [loadingW, setLoadingW] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/admin/waitlist`, { headers }).then(({ data }) => setEntries(data)).catch(err => console.error('API request failed:', err)).finally(() => setLoadingW(false));
  }, [headers]);

  if (loadingW) return <p className="text-[#999] text-sm font-sans">Loading...</p>;

  const sourceLabel = (s) => ({ 'hair-waitlist': 'Hair Waitlist', 'shop-coming-soon': 'Shop — Coming Soon', 'newsletter': 'Newsletter' }[s] || s);

  return (
    <div data-testid="waitlist-tab">
      <h2 className="text-lg font-serif font-medium text-[#1A1A1A] mb-5">Waitlist Signups ({entries.length})</h2>
      {entries.length === 0 ? (
        <div className="bg-white rounded-[2px] border border-[#E8ECF0] p-10 text-center">
          <Mail size={32} className="text-[#ccc] mx-auto mb-3" />
          <p className="text-[#999] font-sans text-sm">No signups yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-[2px] border border-[#E8ECF0] overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-[#E8ECF0] bg-[#F5F7FA]">
                <th className="px-4 py-3 text-[10px] font-sans font-semibold uppercase tracking-[1.5px] text-[#999]">Email</th>
                <th className="px-4 py-3 text-[10px] font-sans font-semibold uppercase tracking-[1.5px] text-[#999]">Source</th>
                <th className="px-4 py-3 text-[10px] font-sans font-semibold uppercase tracking-[1.5px] text-[#999]">Date</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((w, i) => (
                <tr key={w.id || i} data-testid={`waitlist-row-${i}`} className="border-b border-[#F0F2F5] last:border-0">
                  <td className="px-4 py-3 text-[13px] font-sans text-[#1A1A1A]">{w.email}</td>
                  <td className="px-4 py-3"><span className="text-[10px] font-sans font-medium uppercase tracking-[1px] px-2 py-0.5 rounded-[2px] bg-[#EAF4FB] text-[#0C447C]">{sourceLabel(w.source)}</span></td>
                  <td className="px-4 py-3 text-[12px] font-sans text-[#999]">{w.created_at ? new Date(w.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============ STATS TAB ============

function StatsTab({ headers }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/admin/stats`, { headers }).then(({ data }) => setStats(data)).catch(err => console.error('API request failed:', err));
  }, [headers]);

  if (!stats) return <p className="text-[#999] text-sm font-sans">Loading...</p>;

  const cards = [
    { label: 'Products', value: stats.products, icon: Package, color: '#0C447C' },
    { label: 'Orders', value: stats.orders || 0, icon: ShoppingCart, color: '#0E7C61' },
    { label: 'Revenue', value: `\u20b9${stats.total_revenue || 0}`, icon: IndianRupee, color: '#2D6A4F' },
    { label: 'Contact Messages', value: stats.contacts, icon: MessageSquare, color: '#6B4C8A' },
    { label: 'Science Articles', value: stats.articles, icon: FileText, color: '#8B5E3C' },
  ];

  return (
    <div>
      <h2 className="text-lg font-serif font-medium text-[#1A1A1A] mb-5">Overview</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {cards.map(c => (
          <div key={c.label} data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, '-')}`} className="bg-white rounded-[2px] border border-[#E8ECF0] p-6">
            <c.icon size={20} style={{ color: c.color }} className="mb-3" />
            <p className="font-serif text-[32px] text-[#1A1A1A] font-medium leading-none">{c.value}</p>
            <p className="text-[11px] font-sans uppercase tracking-[2px] text-[#999] mt-2">{c.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ SETTINGS TAB ============

function SettingsTab({ headers }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      }, { headers });
      setSuccess('Password changed successfully!');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) {
      setError(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <div>
      <h2 className="text-lg font-serif font-medium text-[#1A1A1A] mb-5">Settings</h2>

      <div className="bg-white rounded-[2px] border border-[#E8ECF0] p-6 max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-9 h-9 rounded-[2px] bg-[#EBF0F5] flex items-center justify-center">
            <Lock size={16} className="text-[#0C447C]" />
          </div>
          <div>
            <h3 className="font-serif text-[15px] text-[#1A1A1A] font-medium">Change Password</h3>
            <p className="text-[11px] text-[#999] font-sans">Update your admin account password</p>
          </div>
        </div>

        <StatusMsg error={error} success={success} />

        <form onSubmit={handleChangePassword} data-testid="change-password-form">
          <div className="mb-4">
            <label className="block text-[11px] font-sans font-medium uppercase tracking-[2px] text-[#0C447C] mb-1.5">Current Password</label>
            <input
              data-testid="current-password-input"
              type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required
              className="w-full px-3 py-2.5 border border-[#E0E0E0] rounded-[2px] text-sm font-sans text-[#1A1A1A] focus:outline-none focus:border-[#0C447C] transition-colors"
              placeholder="Enter current password"
            />
          </div>
          <div className="mb-4">
            <label className="block text-[11px] font-sans font-medium uppercase tracking-[2px] text-[#0C447C] mb-1.5">New Password</label>
            <input
              data-testid="new-password-input"
              type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required
              className="w-full px-3 py-2.5 border border-[#E0E0E0] rounded-[2px] text-sm font-sans text-[#1A1A1A] focus:outline-none focus:border-[#0C447C] transition-colors"
              placeholder="Enter new password"
            />
          </div>
          <div className="mb-6">
            <label className="block text-[11px] font-sans font-medium uppercase tracking-[2px] text-[#0C447C] mb-1.5">Confirm New Password</label>
            <input
              data-testid="confirm-password-input"
              type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required
              className="w-full px-3 py-2.5 border border-[#E0E0E0] rounded-[2px] text-sm font-sans text-[#1A1A1A] focus:outline-none focus:border-[#0C447C] transition-colors"
              placeholder="Confirm new password"
            />
          </div>
          <button
            data-testid="change-password-btn"
            type="submit" disabled={saving}
            className="flex items-center gap-2 bg-[#0C447C] text-white px-6 py-2.5 rounded-[2px] text-[11px] font-sans font-medium uppercase tracking-[2px] hover:bg-[#082F5A] transition-colors disabled:opacity-50"
          >
            <Save size={14} /> {saving ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
